import React, { useState, useEffect } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { FormControl, NativeSelect } from '@material-ui/core';
import PropTypes from 'prop-types';
import _has from 'lodash/has';
import cx from 'classnames';
import { useTranslation } from 'next-i18next';

const useStyles = makeStyles(() => ({
  formControl: {
    width: '100%',
    border: '1px solid #E1E4EB',
    marginBottom: 12,
    borderRadius: 8,
    maxWidth: 357,
  },
  label: {
    color: '#808EAA',
    fontSize: 12,
    lineHeight: '16.8px',
    margin: '3px 0 0 11px',
  },
  select: {
    padding: '3px 11px',
  },
  selectMenu: {
    color: '#87909D',
    fontSize: 12,
    padding: '7px 11px',
  },
  error: {
    color: '#d73a49',
  },
}));

const SelectBox = ({
  customClasses = {
    selectMenu: {},
  },
  label = '',
  options = [],
  rules = {},
  errors,
  name,
  register,
  callback,
  value = '',
  required = false,
  loading = false,
  withOptGroup = false,
}) => {
  const classes = useStyles();
  const [selectValue, setSelectValue] = useState('');
  const [selectOptions, setSelectOptions] = useState(options);
  const [isLoading, setIsLoading] = useState(loading);
  const { t } = useTranslation('common');

  useEffect(() => {
    setIsLoading(loading);
  }, [loading]);

  useEffect(() => {
    setSelectOptions(options);
  }, [options]);

  useEffect(() => {
    setSelectValue(value);
  }, [value]);

  const onChange = e => {
    setSelectValue(e.target.value);
    if (typeof callback === 'function') {
      callback(e.target.value);
    }
  };

  return (
    <FormControl
      variant="outlined"
      className={classes.formControl}
      error={_has(errors, [name])}
      required={required}
    >
      {label && <p className={classes.label}>{label}</p>}
      <NativeSelect
        value={selectOptions.length > 0 ? selectValue : ''}
        variant="outlined"
        onChange={onChange}
        name={name}
        required={required}
        inputProps={{
          name,
          required,
          inputRef: ref => {
            if (!ref) return;
            if (typeof register === 'function') {
              register(ref, rules);
            }
          },
        }}
        classes={{
          select: cx(classes.select),
          selectMenu: [classes.selectMenu, customClasses.selectMenu].join(' '),
        }}
      >
        {withOptGroup
          ? !isLoading &&
            selectOptions.length > 0 &&
            selectOptions.map((parent, k) => (
              <optgroup label={parent.label} key={k}>
                {parent.states.map(({ value, label }, i) => (
                  <option value={value || label} key={i}>
                    {label || t('please-select')}
                  </option>
                ))}
              </optgroup>
            ))
          : !isLoading &&
            selectOptions.length > 0 &&
            selectOptions.map(({ value, label }, i) => (
              <option value={value || label} key={i}>
                {label || t('please-select')}
              </option>
            ))}
      </NativeSelect>
      {errors && errors[name] ? <span className={classes.error}>{errors[name].message}</span> : ''}
    </FormControl>
  );
};

SelectBox.propTypes = {
  customClasses: PropTypes.object,
  label: PropTypes.string,
  value: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.array,
  errors: PropTypes.object,
  rules: PropTypes.object,
  name: PropTypes.string,
  required: PropTypes.bool,
  register: PropTypes.func,
  callback: PropTypes.func,
  loading: PropTypes.bool,
  withOptGroup: PropTypes.bool,
};

export default SelectBox;
